/**
 * A mirrored template, as a message rather than as Meta's component list.
 *
 * ★ PURE, AND KEPT AWAY FROM ANY API MODULE. The campaign wizard and the
 * template editor both need to show "what this will look like on the phone",
 * and both had their own idea of how. This is the one answer, and it imports
 * nothing that the CRA test runner cannot load (see the ESM trap noted on
 * `api/apiSlice`), so the substitution rules are assertable in isolation.
 *
 * Meta describes a template as an ordered list: HEADER, BODY, FOOTER, BUTTONS.
 * A phone renders it top to bottom in that order — footer BEFORE buttons, which
 * is the detail `buildTemplateComponents` in the editor is careful about and
 * the detail a preview most often gets wrong.
 */

/** `{{1}}`, `{{first_name}}` — positional or named. */
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * Split a template into the four parts a bubble renders.
 *
 * Tolerant on purpose: a mirror row from an older sync may carry no
 * `components` at all, and a preview that throws is worse than an empty one.
 *
 * @param {object} template  a mirror row, or anything with `components`
 * @returns {{header: string, headerKind: string, body: string, footer: string,
 *   buttons: Array<{type: string, text: string}>}}
 */
export function partsOf(template) {
	const parts = { header: '', headerKind: 'none', body: '', footer: '', buttons: [] };
	for (const c of Array.isArray(template?.components) ? template.components : []) {
		const type = String(c?.type ?? '').toUpperCase();
		if (type === 'HEADER') {
			const format = String(c.format ?? 'TEXT').toLowerCase();
			parts.headerKind = format;
			// A media header has no words; the bubble shows a placeholder for it.
			parts.header = format === 'text' ? String(c.text ?? '') : '';
		} else if (type === 'BODY') {
			parts.body = String(c.text ?? '');
		} else if (type === 'FOOTER') {
			parts.footer = String(c.text ?? '');
		} else if (type === 'BUTTONS') {
			parts.buttons = (Array.isArray(c.buttons) ? c.buttons : [])
				.filter((b) => b && String(b.text ?? '').trim())
				.map((b) => ({ type: String(b.type ?? 'QUICK_REPLY').toUpperCase(), text: String(b.text).trim() }));
		}
	}
	return parts;
}

/**
 * Replace each placeholder with what the reader would see.
 *
 * A placeholder with no fill is left as written — `{{1}}` on screen is an
 * honest "not decided yet", where a blank would read as a finished message
 * with a word missing.
 *
 * @param {string} text
 * @param {Record<string, string>} fills  key → display text
 */
export function substitute(text, fills = {}) {
	return String(text ?? '').replace(PLACEHOLDER, (match, key) => {
		const value = fills[key];
		return value === undefined || value === null || String(value) === '' ? match : String(value);
	});
}

/**
 * What each variable will show as, from a campaign's `paramMap`.
 *
 * A mapping's *source* is a CRM field, so the preview cannot know the real
 * value — it shows the field's label in the reader's place, which is what an
 * operator wants to check ("is that the first name or the full name?"). A
 * typed literal shows itself. With no source chosen, the fallback stands in,
 * because that is exactly what the recipient gets when the CRM has nothing.
 *
 * @param {string[]} keys           `requiredKeys(template)` — 'header', '1', 'first_name', 'button:0'
 * @param {object} paramMap         `{ [key]: { source, fallback } }`
 * @param {Record<string,string>} labels  source id → human label, e.g. 'lead.first_name' → 'First name'
 * @returns {Record<string, string>}  fills for `substitute`, keyed the way the text spells them
 */
export function fillsFrom(keys, paramMap = {}, labels = {}) {
	const fills = {};
	for (const key of keys ?? []) {
		const entry = paramMap?.[key] ?? {};
		const source = String(entry.source ?? '');
		let shown = '';
		if (source.startsWith('literal:')) shown = source.slice('literal:'.length);
		else if (source) shown = labels[source] ? `[${labels[source]}]` : `[${source}]`;
		else if (entry.fallback) shown = String(entry.fallback);
		if (!shown) continue;
		// The body text spells a header variable `{{1}}` inside the header
		// component, separately from body numbering; `header` is its own key.
		fills[key === 'header' ? '1' : key] = shown;
	}
	return fills;
}

/** The first line of a body, for a picker card. */
export function excerpt(text, max = 96) {
	const one = String(text ?? '').replace(/\s+/g, ' ').trim();
	return one.length > max ? `${one.slice(0, max - 1)}…` : one;
}
